import { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import type { AppDispatch } from '@/app/store/store';
import { buildLandingHref } from '@/core/utils/apiUtils';
import { AppLink } from '@/shared/components/AppLink';
import { bannerWidgetBuild } from '@/shared/features/banner-widget/config/bannerWidget.build';
import { useBannerWidgetBanners } from '@/shared/features/banner-widget/presentation/hooks/useBannerWidgetBanners';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';

interface BannerWidgetSlotProps {
  fullWidth?: boolean;
  /** Optional slot id (e.g. for key); each mount picks a random banner. */
  slotId?: string;
}

/**
 * Banner widget slot. Picks one random banner when it mounts (when banners are available).
 * Remount (e.g. parent changes key when slot enters view) to show a new random banner.
 */
export function BannerWidgetSlot({
  fullWidth = false,
}: BannerWidgetSlotProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => bannerWidgetBuild(dispatch), [dispatch]);
  const { banners, isLoading } = useBannerWidgetBanners({ service });
  const { analytics } = useAnalytics();

  const handleBannerClick = () => {
    if (!displayBanner) return;
    analytics?.trackEvent('Banner Clicked', {
      'Banner Name': displayBanner.name,
      'Banner Type': displayBanner.bannerType,
      'Banner Position': 'Widget Banner',
    });
  };

  const lastIsIntersectingRef = useRef<boolean | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [bannerIndex, setBannerIndex] = useState<number | null>(null);

  const { ref: containerRef } = useIntersectionObserver<HTMLDivElement>(
    { threshold: 0 },
    (entry) => {
      if (!banners || banners.length === 0) {
        return;
      }

      const isIntersectingNow = entry.isIntersecting;
      const wasIntersecting = lastIsIntersectingRef.current;

      // First observer callback: pick an initial banner even before it enters the viewport.
      if (wasIntersecting === null) {
        const idx = Math.floor(Math.random() * banners.length);
        setBannerIndex(idx);
        lastIsIntersectingRef.current = isIntersectingNow;
        return;
      }

      // When the slot leaves the viewport (visible -> not visible), prepare the next banner
      // while it's off-screen so the change happens before the user sees it again.
      if (!isIntersectingNow && wasIntersecting) {
        const idx = Math.floor(Math.random() * banners.length);
        setBannerIndex(idx);
      }

      lastIsIntersectingRef.current = isIntersectingNow;
    },
  );

  const displayBanner =
    banners && bannerIndex != null && banners.length > 0
      ? banners[Math.min(bannerIndex, banners.length - 1)]
      : null;

  const href = displayBanner ? buildLandingHref(displayBanner.landingUrl) : null;

  const skeletonClass = fullWidth
    ? 'absolute inset-0 animate-pulse rounded-none bg-gray-200'
    : 'absolute inset-0 animate-pulse rounded-md bg-gray-200';

  if (fullWidth) {
    return (
      <div className="w-full" ref={containerRef}>
        {isLoading || !displayBanner ? (
          // No banner yet — reserve space only while loading; hide entirely when empty
          isLoading ? (
            <div className="w-full shrink-0 aspect-[141/92] animate-pulse bg-gray-200" />
          ) : null
        ) : href ? (
          <AppLink to={href} className="block w-full" onClick={handleBannerClick}>
            <div className="relative w-full shrink-0 aspect-[141/92]">
              {!imageLoaded && <div className={skeletonClass} aria-hidden />}
              <picture className="relative h-full w-full">
                {displayBanner.desktopImageUrl && (
                  <source media="(min-width: 1024px)" srcSet={displayBanner.desktopImageUrl} />
                )}
                <img
                  src={displayBanner.imageUrl}
                  alt={displayBanner.name}
                  className="relative h-full w-full object-fill object-center transition-opacity duration-200"
                  style={{ opacity: imageLoaded ? 1 : 0 }}
                  loading="lazy"
                  onLoad={() => setImageLoaded(true)}
                />
              </picture>
            </div>
          </AppLink>
        ) : (
          <div className="relative w-full shrink-0 aspect-[141/92]">
            {!imageLoaded && <div className={skeletonClass} aria-hidden />}
            <picture className="relative h-full w-full">
              {displayBanner.desktopImageUrl && (
                <source media="(min-width: 1024px)" srcSet={displayBanner.desktopImageUrl} />
              )}
              <img
                src={displayBanner.imageUrl}
                alt={displayBanner.name}
                className="relative h-full w-full object-fill object-center transition-opacity duration-200"
                style={{ opacity: imageLoaded ? 1 : 0 }}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
              />
            </picture>
          </div>
        )}
      </div>
    );
  }

  const tileContent =
    !displayBanner || isLoading ? null : (
      <div className="relative h-full w-full shrink-0 overflow-hidden rounded-md">
        {!imageLoaded && <div className={skeletonClass} aria-hidden />}
        <picture className="relative h-full w-full">
          {displayBanner.desktopImageUrl && (
            <source media="(min-width: 1024px)" srcSet={displayBanner.desktopImageUrl} />
          )}
          <img
            src={displayBanner.imageUrl}
            alt={displayBanner.name}
            className="relative h-full w-full object-fill object-center transition-opacity duration-200"
            style={{ opacity: imageLoaded ? 1 : 0 }}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
          />
        </picture>
      </div>
    );

  return (
    <div className="relative flex h-full w-full shrink-0" ref={containerRef}>
      {isLoading || !displayBanner ? (
        isLoading ? (
          <div className="h-full w-full animate-pulse rounded-md bg-gray-200" />
        ) : null
      ) : href ? (
        <AppLink
          to={href}
          className="flex h-full w-full items-center justify-center rounded-md border border-gray-200 bg-white"
          onClick={handleBannerClick}
        >
          {tileContent}
        </AppLink>
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-md border border-gray-200 bg-white">
          {tileContent}
        </div>
      )}
    </div>
  );
}

